import { z } from 'zod';
import { STAFF_ROLES } from '../constants.js';
import { emailSchema, passwordSchema } from './common.js';
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(200),
});
export const customerRegisterSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    name: z.string().trim().min(1).max(120),
    phone: z.string().trim().min(5).max(32).optional(),
    /**
     * A friend's invite code. Optional, and never fatal: refusing to create an account
     * because a referral code was mistyped loses a customer to save a discount.
     */
    referralCode: z.string().trim().max(24).optional().or(z.literal('')),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(10).max(500),
});
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z.object({
    token: z.string().min(10).max(500),
    password: passwordSchema,
});
export const acceptInviteSchema = z.object({
    token: z.string().min(10).max(500),
    name: z.string().trim().min(1).max(120),
    password: passwordSchema,
});
export const staffRoleSchema = z.enum(STAFF_ROLES);
/**
 * What an account holder may change about themselves.
 *
 * Email is absent on purpose. It is the login identity and half of a unique constraint,
 * so changing it without a verification round trip is how somebody locks themselves out
 * of an account or claims an address they do not control. That is its own feature.
 */
/**
 * Every field below this line follows one rule: ABSENT leaves the stored value alone,
 * EMPTY clears it. Without that distinction a form that posts only what it shows would
 * silently wipe whatever it did not render, and a partial update is the normal case.
 */
const clearable = (max) => z.string().trim().max(max).optional();
export const profileUpdateSchema = z.object({
    name: z.string().trim().min(2).max(80),
    // Empty clears it: a phone number that is no longer current is worse than none.
    phone: clearable(32),
    // --- Passenger details ------------------------------------------------------------
    // Unverified, and matched against nothing. These exist so a passenger carries what an
    // operator asks for at boarding without retyping it into every booking.
    //
    // A calendar date, not an instant: a birthday has no time and no zone, and parsing one
    // as a timestamp moves it across midnight for anybody east or west of the server.
    dateOfBirth: z
        .string()
        .trim()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
        .optional()
        .or(z.literal('')),
    idNumber: clearable(40),
    addressLine1: clearable(120),
    addressLine2: clearable(120),
    city: clearable(80),
    region: clearable(80),
    postalCode: clearable(20),
    /** ISO 3166-1 alpha-2, upper-cased so one country has one spelling. */
    country: z
        .string()
        .trim()
        .length(2)
        .transform((value) => value.toUpperCase())
        .optional()
        .or(z.literal('')),
    emergencyName: clearable(80),
    emergencyPhone: clearable(32),
});
/**
 * Replacing your own password.
 *
 * The current password is optional because the caller may be here on a temporary one, which they
 * proved at login moments ago and which is the only thing their session can be used for. An
 * ordinary session must still supply it — otherwise a stolen access token becomes a password
 * change, which is an account takeover rather than an inconvenience.
 */
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(200).optional(),
    newPassword: passwordSchema,
});
//# sourceMappingURL=auth.js.map